import { useMutation, useQueryClient } from '@tanstack/react-query';
import { enqueueSnackbar } from 'notistack';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

// common
import { KEYS_QUERY } from 'common/constants';
import useGeneralHook from 'common/hook/useGeneralHook';
import useService from 'common/hook/useService';
import { AppState } from '@redux/store';
import { setUserRoles } from '@redux/authReducer';

// screen
import { handleException } from 'modules/account/detail/helpers/utils';
import type { GovAgencyItems, RoleAssignmentItem } from 'modules/account/types';

interface MutatingProps {
  userId: string;
  rolesAssignment: {
    forClientBusiness: RoleAssignmentItem[];
    forGovAgencyBusiness: GovAgencyItems;
    forAdmin: RoleAssignmentItem[];
    forSystem: RoleAssignmentItem[];
  };
}

const useUpdateRoles = () => {
  const queryClient = useQueryClient();
  const { formatMessage } = useIntl();
  const { userInfo } = useSelector(({ auth }: AppState) => auth);
  const { dispatch } = useGeneralHook();
  const service = useService();

  return useMutation({
    mutationFn: async ({ userId, rolesAssignment }: MutatingProps) => {
      try {
        if (!service || !userId) return null;

        const roleAssignment = {
          forClientBusiness: rolesAssignment.forClientBusiness.map((r) => ({
            roleId: r.roleId,
            orgId: r.orgId,
          })),
          forAdmin: rolesAssignment.forAdmin.map((r) => ({
            roleId: r.roleId,
            orgId: r.orgId,
          })),
          forSystem: rolesAssignment.forSystem.map((r) => ({
            roleId: r.roleId,
            orgId: r.orgId,
          })),
          forGovAgencyBusiness: rolesAssignment.forGovAgencyBusiness.map((item) => ({
            roleId: item.roleId,
            orgId: item.orgId,
            includedClientByBoundary: item.includedClientByBoundary,
            includedClientIds: item.includedClientIds ? [...item.includedClientIds] : null,
          })),
        };

        const res = await service.UserManagementController_editUserRoles({
          params: { userId },
          body: { roleAssignment },
        });

        if (userInfo && userInfo.id === userId) {
          const userRoles = await service.UserController_getRoleAssignments();
          dispatch(setUserRoles(userRoles));
        }

        if (!res) return null;
        return { message: 'successful' };
      } catch (error) {
        const axiosError = handleException(error, { notifyUser: false });

        if (axiosError && axiosError === 403) {
          enqueueSnackbar({
            message: formatMessage({ id: 'account.error.notAllowed' }),
            variant: 'error',
          });
        } else {
          enqueueSnackbar({
            message: formatMessage({ id: 'account.error.failToUpdateUserRoles' }),
            variant: 'error',
          });
        }
        throw error;
      }
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [KEYS_QUERY.accountRoles, variables.userId] });
    },
  }).mutateAsync;
};

export default useUpdateRoles;
